import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { Button, Modal } from 'react-bootstrap';
import {
  deleteMedia,
  getUploadStatus,
  updateFileFieldValue,
  uploadImages,
} from '@/features/orders/ordering/services/orderMediaService.js';
import LoadingIndicator from '@/components/LoadingIndicator.js';
import './UploadComponent.scss';
import {
  IMAGE_PROCESSING_STATUS_TYPES,
  ALL_TABLE_TYPES,
} from '@/constants/constants.js';
import { v4 as uuidv4 } from 'uuid';
import localforage from 'localforage';

// 判断是否为 URL 的简单正则表达式
const isUrl = (str) => /^(https?:\/\/)/.test(str);

const IMAGE_PREVIEW_CACHE_KEY = 'image_previews';

const openDb = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('ImagePreviewsDB', 1);

    request.onerror = (event) => reject(event.target.error);

    request.onsuccess = (event) => resolve(event.target.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('previews')) {
        db.createObjectStore('previews', { keyPath: 'id' });
      }
    };
  });
};

const saveImagePreview = async (imageId, compressedImageBlob) => {
  try {
    // 等待 openDb 函数执行完成
    const db = await openDb();
    const transaction = db.transaction('previews', 'readwrite');
    const store = transaction.objectStore('previews');

    const imagePreview = {
      id: imageId,
      image: compressedImageBlob,
      timestamp: Date.now(),
    };

    // 返回一个 Promise
    return new Promise((resolve, reject) => {
      const request = store.put(imagePreview);

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(event.target.error);
    });
  } catch (error) {
    console.error('保存图片预览失败:', error);
    throw error;
  }
};

const compressImage = (file, maxWidth = 300) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onloadend = () => {
      img.src = reader.result;

      img.onload = () => {
        // Calculate the ratio to maintain aspect ratio
        const ratio = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas size to new scaled image dimensions
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        // Draw image on canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Convert canvas to base64
        canvas.toBlob(
          (blob) => resolve(blob),
          'image/jpeg',
          0.8 // Compression quality (0 to 1)
        );
      };

      img.onerror = reject;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const cacheImagePreview = async (imageId, file) => {
  try {
    // 压缩图片
    const compressedImage = await compressImage(file);

    // 存储压缩后的图片预览到 IndexedDB
    await saveImagePreview(imageId, compressedImage);
  } catch (error) {
    console.error('压缩图片或存储预览失败:', error);
  }
};

// 获取缓存中的预览图
const getCachedPreview = async (imageId) => {
  const db = await openDb();
  const transaction = db.transaction('previews', 'readonly');
  const store = transaction.objectStore('previews');

  return new Promise((resolve, reject) => {
    const request = store.get(imageId);

    request.onsuccess = (event) => {
      const result = event.target.result;
      if (result && Date.now() - result.timestamp < 3600000) {
        // 返回压缩后的图片预览
        resolve(result.image);
      } else {
        resolve(null); // 缓存过期
      }
    };

    request.onerror = (event) => reject(event.target.error);
  });
};

// 清理指定缓存
const clearCachedPreview = async (imageId) => {
  const cache = (await localforage.getItem(IMAGE_PREVIEW_CACHE_KEY)) || {};
  delete cache[imageId];
  await localforage.setItem(IMAGE_PREVIEW_CACHE_KEY, cache);
};

const ImageUpload = ({
  label,
  client_id,
  onUpload,
  onDelete,
  onReplace,
  onUpdateImages,
  files = [],
  isDisabled,
  totalImagesLimit,
  dynamicImageLimit,
  required = true,
}) => {
  const user_id = sessionStorage.getItem('userid');
  const inputRefs = useRef([]);
  const fileInputRefs = useRef([]);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const [imageStates, setImageStates] = useState([]);

  const imageStatesRef = useRef(imageStates);
  const [loadingImages, setLoadingImages] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [duplicateModal, setDuplicateModal] = useState({ show: false, message: '' });
  const originalSortOrdersRef = useRef({});

  useEffect(() => {
    imageStatesRef.current = imageStates;
  }, [imageStates]);

  useEffect(() => {
    const fetchPreviews = async () => {
      if (isInitialLoad) {
        const newImageStates = await Promise.all(
          files.filter(Boolean).map(async (file) => {
            let cachedPreview = null;
            if (file.status === IMAGE_PROCESSING_STATUS_TYPES.UPLOADING) {
              cachedPreview = await getCachedPreview(file.id);
            }
            return {
              id: file.id,
              content: cachedPreview || file.content,
              status:
                file.status ||
                (cachedPreview
                  ? IMAGE_PROCESSING_STATUS_TYPES.UPLOADING
                  : isUrl(file.content)
                    ? IMAGE_PROCESSING_STATUS_TYPES.SUCCEED
                    : IMAGE_PROCESSING_STATUS_TYPES.FAILED),
              error: '',
              description: file.description,
              sort_order: file.sort_order,
              image_dynamic_AI: file.image_dynamic_AI,
            };
          })
        );
        setImageStates(newImageStates);

        const hasPending = newImageStates.some(
          (s) => s.status === IMAGE_PROCESSING_STATUS_TYPES.UPLOADING
        );

        if (hasPending) {
          setLoadingImages(
            newImageStates.filter(
              (s) => s.status === IMAGE_PROCESSING_STATUS_TYPES.UPLOADING
            )
          );
        }

        setIsInitialLoad(false);
      }
    };
    fetchPreviews();
  }, [files, isInitialLoad]);

  useEffect(() => {
    let intervalId;

    // 只有当有 loadingImages 时才启动轮询
    if (loadingImages.length === 0) return;

    const checkAndUpdateStatus = async () => {
      try {
        const newStatus = await getUploadStatus(client_id);
        const currentStates = imageStatesRef.current;

        const updatedStates = currentStates.map((state) => {
          const foundStatus = newStatus.find((s) => s.id === state.id);
          if (!foundStatus) return state;

          // 当上传完成时清理缓存
          if (
            foundStatus.image_url &&
            state.status !== IMAGE_PROCESSING_STATUS_TYPES.SUCCEED
          ) {
            clearCachedPreview(state.id);
          }

          return {
            ...state,
            status: foundStatus.image_url
              ? IMAGE_PROCESSING_STATUS_TYPES.SUCCEED
              : state.status,
            content: foundStatus.image_url || state.content,
          };
        });

        setImageStates(updatedStates);
      } catch (error) {
        console.error('状态更新失败', error);
      }
    };

    intervalId = setInterval(checkAndUpdateStatus, 5000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingImages]);

  // 验证图片文件，返回验证状态
  const validateImageFiles = useCallback(
    async (newFiles, totalImagesLimit, isUpload) => {
      const totalLimit = totalImagesLimit;
      const succeedLength = imageStates.filter(
        (state) =>
          state.status === IMAGE_PROCESSING_STATUS_TYPES.SUCCEED ||
          state.status === IMAGE_PROCESSING_STATUS_TYPES.UPLOADING
      ).length;

      if (isUpload && totalLimit !== undefined) {
        if (newFiles.length + succeedLength > totalLimit) {
          alert(`图片总数量不能超过 ${totalImagesLimit} 张`);
          return null;
        }
      }

      const validatedFiles = newFiles.map((file) => {
        const errors = [];

        // 格式验证
        if (!/\.(jpg|jpeg|png|heif|webp)$/i.test(file.name)) {
          errors.push('格式不支持');
        }

        // 大小验证
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > 25) {
          errors.push('文件过大');
        }
        return { file, errors };
      });

      if (isUpload) {
        const maxSortOrder = imageStates.reduce((max, state) => Math.max(max, state.sort_order || 0), 0);
        const newImagePromises = validatedFiles.map(
          async (validatedFile, index) => {
            const newId = `${client_id}-${uuidv4()}`;

            // 保存预览到本地缓存
            if (validatedFile.errors.length === 0) {
              await cacheImagePreview(newId, validatedFile.file);
            }

            return {
              id: newId,
              content: validatedFile.file, // 保持File对象用于上传
              status:
                validatedFile.errors.length === 0
                  ? IMAGE_PROCESSING_STATUS_TYPES.UPLOADING
                  : IMAGE_PROCESSING_STATUS_TYPES.FAILED,
              error: validatedFile.errors.join(', '),
              description: '',
              sort_order: maxSortOrder + index + 1,
              image_dynamic_AI: false,
            };
          }
        );

        const newImageStates = await Promise.all(newImagePromises);
        setImageStates((prev) => [...prev, ...newImageStates]);
        return newImageStates;
      } else {
        return validatedFiles;
      }
    },
    [imageStates, client_id]
  );

  // 将 uploadFile 函数包裹在 useCallback 中
  const uploadFile = useCallback(
    async (file, client_id, id, id_upload, sort_order) => {
      const formData = new FormData();
      if (client_id) formData.append('client_id', client_id);
      formData.append('images', file);
      formData.append('id', id);
      formData.append('user_id', user_id);
      formData.append('is_upload', id_upload);
      if (sort_order !== undefined) {
        formData.append('sort_order', sort_order);
      }
      try {
        const response = await uploadImages(formData);
        return response.data;
      } catch (error) {
        console.error('文件上传至oss失败:', error);
        throw new Error('文件上传至oss失败:', error);
      }
    },
    [user_id]
  );

  const processValidFiles = useCallback(
    async (validatedFiles, client_id, is_upload) => {
      // 分离出有效文件
      const validFilesCount = validatedFiles.filter(
        (validatedFile) => validatedFile.error === ''
      ).length;
      if (validFilesCount === 0)
        return { success: false, errorMessage: '没有有效文件' };

      const successfulUploads = [];
      const failedUploads = [];
      let newImageStates = [...validatedFiles];

      await Promise.all(
        validatedFiles.map(async (file, index) => {
          if (file && file.status === IMAGE_PROCESSING_STATUS_TYPES.UPLOADING) {
            const currentId = file.id;
            try {
              const response = await uploadFile(
                file.content,
                client_id,
                file.id,
                is_upload,
                file.sort_order
              );
              // 获取最新的 imageStates 判断是否存在当前 ID
              const currentStates = imageStatesRef.current;
              if (currentStates.some((state) => state.id === currentId)) {
                const newImageStates = [...imageStatesRef.current];
                const updatedImageState = newImageStates.find(
                  (state) => state.id === currentId
                );
                if (updatedImageState) {
                  updatedImageState.content = response.imageUrl;
                  updatedImageState.status =
                    IMAGE_PROCESSING_STATUS_TYPES.SUCCEED;
                  updatedImageState.error = '';
                  setImageStates(newImageStates);
                  onUpload(newImageStates, client_id);
                }
              }
            } catch (error) {
              const currentStates = imageStatesRef.current;
              if (currentStates.some((state) => state.id === currentId)) {
                let newImageStates = [...currentStates];
                const failedImage = newImageStates.find(
                  (state) => state.id === currentId
                );
                if (failedImage) {
                  failedImage.status = IMAGE_PROCESSING_STATUS_TYPES.FAILED;
                  failedImage.error = '文件上传失败，请稍后重试';
                  setImageStates(newImageStates);
                }
              }
            }
          }
        })
      );

      if (successfulUploads.length > 0) {
        onUpload(newImageStates, client_id);
        // setIsEditing((prev) => [...prev, ...newFiles.map(() => false)]);
      }

      if (failedUploads.length > 0) {
        setErrorMessage(`部分文件上传失败: ${failedUploads.join(', ')}`);
        return {
          success: false,
          errorMessage: `部分文件上传失败: ${failedUploads.join(', ')}`,
        };
      }
      return { success: true };
    },
    [uploadFile, onUpload]
  );

  const handleFileUpload = useCallback(
    async (event) => {
      const newFiles = Array.from(event.target.files);
      if (newFiles.length === 0) return;
      try {
        const validationResult = await validateImageFiles(
          newFiles,
          totalImagesLimit,
          true
        );

        // 如果 validateImageFiles 返回 null，说明图片数量超过限制，直接返回
        if (validationResult === null) {
          event.target.value = '';
          return;
        }
        setErrorMessage('');
        await processValidFiles(validationResult, client_id, true, true);
      } catch (error) {
        console.error('文件上传失败:', error);
        setErrorMessage(`文件上传失败，请稍后重试 (${error})`);
      } finally {
        event.target.value = '';
      }
    },
    [validateImageFiles, totalImagesLimit, processValidFiles, client_id]
  );

  const handleFileReplace = useCallback(
    async (event, index, id, id_upload) => {
      const newFile = event.target.files[0];
      if (!newFile) return;

      try {
        const validationResult = await validateImageFiles(
          [newFile],
          totalImagesLimit,
          false
        );
        if (validationResult[0]?.errors?.length > 0) {
          const errorMessage = validationResult[0]?.errors[0];
          setErrorMessage(`文件替换失败：${errorMessage}`);
          event.target.value = '';
          return;
        }

        let newImageStates = [...imageStates];
        newImageStates[index].status = IMAGE_PROCESSING_STATUS_TYPES.UPLOADING;
        newImageStates[index].error = '';

        setImageStates(newImageStates);

        const response = await uploadFile(
          newFile,
          client_id,
          id,
          id_upload,
          newImageStates[index].sort_order
        );
        setErrorMessage('');
        if (response) {
          const updatedImageState = newImageStates.find(
            (state) => state.id === response.id
          );
          if (updatedImageState) {
            // 更新对应的图片状态对象的内容和状态
            updatedImageState.content = response.imageUrl;
            updatedImageState.status = IMAGE_PROCESSING_STATUS_TYPES.SUCCEED;
            updatedImageState.error = '';

            // 更新 imageStates
            setImageStates([...newImageStates]);

            // 调用 onReplace 函数，传递新的图片 URL 和对应的 id
            onReplace(response.imageUrl, response.id);
          }
          setErrorMessage('');
        }
      } catch (error) {
        console.error('文件替换失败:', error);
      } finally {
        let newImageStates = [...imageStates];
        setImageStates(newImageStates);
        event.target.value = '';
      }
    },
    [
      validateImageFiles,
      totalImagesLimit,
      imageStates,
      uploadFile,
      client_id,
      onReplace,
    ]
  );

  const handleFileDelete = useCallback(
    async (id) => {
      const imageToDelete = imageStates.find((state) => state.id === id);
      if (!imageToDelete) {
        return;
      }
      // 只解构出 url，id 直接使用参数中的 id
      const { content } = imageToDelete;
      try {
        await deleteMedia(ALL_TABLE_TYPES.image, id, content);
        // 根据 id 过滤出需要保留的图片状态
        const newImageStates = imageStates.filter((state) => state.id !== id);
        // 更新图片状态
        setImageStates(newImageStates);
        onDelete(newImageStates);
      } catch (error) {
        console.error('删除图片失败:', error);
      }
    },
    [imageStates, onDelete]
  );

  const handleUpdateDescription = useCallback(
    (e, id) => {
      const newDescription = e.target.value;
      const prevImageStates = imageStatesRef.current;
      const updatedImageStates = prevImageStates.map((state) => {
        if (state.id === id) {
          return {
            ...state,
            description: newDescription,
          };
        }
        return state;
      });

      setImageStates(updatedImageStates);
      onUpdateImages(updatedImageStates);
      updateFileFieldValue('images', 'image_description', id, newDescription);
    },
    [setImageStates, onUpdateImages]
  );

  const handleChangeSortOrder = useCallback(
    (e, id) => {
      const rawValue = e.target.value;
      const prevImageStates = imageStatesRef.current;
      const updatedImageStates = prevImageStates.map((state) => {
        if (state.id === id) {
          return {
            ...state,
            sort_order: rawValue === '' ? '' : parseInt(rawValue, 10),
          };
        }
        return state;
      });
      setImageStates(updatedImageStates);
    },
    [setImageStates]
  );

  const handleBlurSortOrder = useCallback(
    (e, id) => {
      let newSortOrder;
      
      if (e.target.value === '') {
        newSortOrder = null;
      } else {
        newSortOrder = parseInt(e.target.value, 10);
        if (isNaN(newSortOrder) || newSortOrder < 0) {
          newSortOrder = 0;
        } else if (newSortOrder > 25) {
          newSortOrder = 25;
        }
      }

      // Check for duplicates
      const isDuplicate = newSortOrder !== null && imageStatesRef.current.some(
        (state) => state.id !== id && state.sort_order === newSortOrder
      );

      if (isDuplicate) {
        setDuplicateModal({
          show: true,
          message: `序号 ${newSortOrder} 已存在，请勿重复设置序号。`,
        });
        newSortOrder = originalSortOrdersRef.current[id] !== undefined ? originalSortOrdersRef.current[id] : null;
      } else {
        originalSortOrdersRef.current[id] = newSortOrder;
      }

      const prevImageStates = imageStatesRef.current;
      const updatedImageStates = prevImageStates.map((state) => {
        if (state.id === id) {
          return {
            ...state,
            sort_order: newSortOrder,
          };
        }
        return state;
      });

      // 按新的 sort_order 重新排序本地显示
      const sortedStates = [...updatedImageStates].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
      );
      
      setImageStates(sortedStates);
      onUpdateImages(sortedStates);
      updateFileFieldValue('images', 'sort_order', id, newSortOrder);
    },
    [setImageStates, onUpdateImages]
  );

  const handleUpdateSelection = useCallback(
    (id, newSelection) => {
      updateFileFieldValue('images', 'image_dynamic_AI', id, newSelection)
        .then(() => {
          const prevImageStates = imageStatesRef.current;
          const updatedImageStates = prevImageStates.map((state) => {
            if (state.id === id) {
              return {
                ...state,
                image_dynamic_AI: newSelection,
              };
            }
            return state;
          });

          setImageStates(updatedImageStates);
          onUpdateImages(updatedImageStates);
        })
        .catch((error) => {
          console.error('更新选择失败:', error);
        });
    },
    [setImageStates, onUpdateImages]
  );

  const imagePreviews = useMemo(
    () =>
      imageStates.map(({ content }) => {
        if (content instanceof Blob) {
          return {
            src: URL.createObjectURL(content),
            isObjectUrl: true,
          };
        }
        return {
          src: typeof content === 'string' ? content : undefined,
          isObjectUrl: false,
        };
      }),
    [imageStates]
  );

  useEffect(
    () => () => {
      imagePreviews.forEach(({ src, isObjectUrl }) => {
        if (isObjectUrl && src) URL.revokeObjectURL(src);
      });
    },
    [imagePreviews]
  );

  const fileItems = useMemo(
    () =>
      imageStates.map((image, index) => (
        <div key={index} className={`file-item image`}>
          <div className="file-container">
            <>
              <div className="image-container">
                {(image.status === IMAGE_PROCESSING_STATUS_TYPES.UPLOADING ||
                  image.status === 'faild' ||
                  image.error) && (
                  <div className="maskLoading-container">
                    {image.error && <div className="error">{image.error}</div>}
                    {image.status ===
                      IMAGE_PROCESSING_STATUS_TYPES.UPLOADING && (
                      <LoadingIndicator
                        label={'图片上传中，请勿操作页面，避免资料遗失。请稍等'}
                      />
                    )}
                    {image.status === 'faild' && (
                      <div className="faild-text ">上传失败，请重新上传</div>
                    )}
                  </div>
                )}

                <img
                  src={imagePreviews[index]?.src}
                  alt={` ${image.description || '图片无描述'}`}
                  className="file-preview-image"
                  style={{
                    objectFit: 'contain',
                    objectPosition: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </div>
            </>
          </div>
          <div className="file-description-container">
            <div className={`file-description`}>
              <>
                <div className="file-description-title">
                  {image.status === IMAGE_PROCESSING_STATUS_TYPES.SUCCEED && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div
                        style={{
                          display: 'flex',
                          flex: '1',
                          flexDirection: 'column',
                        }}
                      >
                        <label>图片描述</label>
                        <input
                          ref={(el) => (inputRefs.current[index] = el)}
                          type="text"
                          className="form-control"
                          value={image.description || ''}
                          onChange={(e) => {
                            handleUpdateDescription(e, image.id);
                          }}
                          disabled={isDisabled}
                          placeholder="请输入图片描述"
                          style={{ flex: 1 }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label>顺序  <span style={{ color: 'red' }}> *</span></label>
                        <input
                          type="number"
                          className="form-control"
                          value={image.sort_order === null || image.sort_order === undefined || image.sort_order === '' ? '' : image.sort_order}
                          onFocus={() => {
                            const current = imageStatesRef.current.find(s => s.id === image.id);
                            if (current) originalSortOrdersRef.current[image.id] = current.sort_order;
                          }}
                          min={0}
                          max={25}
                          onChange={(e) => {
                            handleChangeSortOrder(e, image.id);
                          }}
                          onBlur={(e) => {
                            handleBlurSortOrder(e, image.id);
                          }}
                          disabled={isDisabled}
                          placeholder="请输入序号"
                          title="输入序号以改变图片排列顺序。数值越小越靠前。"
                          style={{ width: '70px' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            </div>
            {dynamicImageLimit !== 0 && (
              <div className="selection-container">
                <input
                  type="checkbox"
                  id={`dynamic-checkbox-${index}`}
                  checked={image.image_dynamic_AI || false}
                  onChange={(e) => {
                    const newImageStates = [...imageStates];
                    const selectedCount = newImageStates.filter(
                      (state) => state.image_dynamic_AI === true
                    ).length;

                    if (
                      e.target.checked &&
                      selectedCount >= dynamicImageLimit
                    ) {
                      alert(`勾选的照片数量不能超过 ${dynamicImageLimit} 张`);
                      return;
                    }

                    const newSelection = e.target.checked;
                    handleUpdateSelection(image.id, newSelection);
                  }}
                  disabled={isDisabled}
                />
                <label
                  htmlFor={`dynamic-checkbox-${index}`}
                  className={isDisabled ? 'disabled' : ''}
                >
                  AI动态化照片
                </label>
              </div>
            )}
            <div className="file-operation-container">
              <>
                <Button
                  type="Button"
                  className="reupload-Button"
                  onClick={() => fileInputRefs.current[index]?.click()}
                  disabled={isDisabled}
                >
                  重新上传
                </Button>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={(el) => (fileInputRefs.current[index] = el)}
                  onChange={(event) =>
                    handleFileReplace(event, index, image.id, false)
                  }
                  disabled={isDisabled}
                />
              </>

              <Button
                type="Button"
                onClick={() => handleFileDelete(image.id)}
                className="delete-Button"
                disabled={isDisabled}
              >
                删除
              </Button>
            </div>
          </div>
        </div>
      )),
    [
      dynamicImageLimit,
      handleFileDelete,
      handleFileReplace,
      handleUpdateDescription,
      handleChangeSortOrder,
      handleBlurSortOrder,
      handleUpdateSelection,
      imagePreviews,
      imageStates,
      isDisabled,
    ]
  );

  return (
    <div className="col-md-12">
      <div>
        <div className={`image-upload-section`}>
          <div
            className="input-section-container"
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <div style={{ display: 'flex' }}>
              <div className="form-label-container">
                <h5>
                  {label} {required && <span style={{ color: 'red' }}> *</span>}
                </h5>
              </div>
              <Button
                type="Button"
                className="add-photo-button"
                onClick={() => fileInputRef.current.click()}
                disabled={isDisabled}
                name="images"
              >
                添加照片
              </Button>
            </div>
            <span style={{ color: 'red' }}>顺序为图片在视频中出现的顺序</span>
          </div>
          {imageStates.length > 0 && (
            <div className="file-preview">{fileItems}</div>
          )}
          <div className="file-upload-container">
            {errorMessage ? (
              <div className="error-message">{errorMessage}</div>
            ) : null}

            <input
              type="file"
              accept="image/*"
              className="form-control"
              multiple={true}
              onChange={(event) => {
                handleFileUpload(event);
              }}
              disabled={isDisabled}
              data-placeholder={'未选择'}
              ref={(el) => (fileInputRef.current = el)}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </div>
      
      <Modal show={duplicateModal.show} onHide={() => setDuplicateModal({ show: false, message: '' })} centered>
        <Modal.Header closeButton>
        </Modal.Header>
        <Modal.Body>{duplicateModal.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setDuplicateModal({ show: false, message: '' })}>
            确定
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ImageUpload;
